import update from 'immutability-helper'
import Inflection from 'inflection'
import * as React from 'react'
import { FormFor } from 'react-rails-form-helpers'
import {
  AbstractMembershipCheckoutForm,
  buildBaseCheckoutState,
  IBaseCheckoutProps,
  IBaseCheckoutState,
} from '../AbstractMembershipCheckoutForm'
import { AddressFormFields } from '../Address/AddressForm'
import * as API from '../lib/api'
import { Checkout } from '../lib/checkout'
import { PurchaseOption } from '../lib/purchase_option'
import { accountFieldName, Step1Account } from './Step1Account'
import {
  billingAddressFieldName,
  BillingCustomerField,
  billingCustomerFieldName,
  Step2Billing,
} from './Step2Billing'
import { RightRail } from './RightRail'
import {
  IStepContext,
  trackStepAdvance,
  trackStepView,
  TRIAL_REDESIGN_EXPERIMENT,
} from './stepAnalytics'
import { Step3Payment } from './Step3Payment'
import { StepAccordion } from './StepAccordion'
import { Timeline } from './Timeline'

declare var Routes: any
declare var $: any

// Must stay in lock-step with the Ruby `RenewalDiscount::DESCRIPTION` constant:
// the serialized `paymentDetails.adjustments` carry the discount under this
// label. A back-end rename silently zeroes the renewal copy;
// `spec/lib/renewal_discount_spec.rb` pins the constant to this literal.
const RENEWAL_DISCOUNT_LABEL = 'Loyalty Discount for Current Members'

export interface IStepDefinition {
  id: string
  label: string
}

export const STEPS: IStepDefinition[] = [
  { id: 'account', label: 'Create Your Account' },
  { id: 'billing', label: 'Billing Information' },
  { id: 'payment', label: 'Payment Information' },
]

export const CASHIER_FORM_NAME = 'cashier'

export interface IProps extends IBaseCheckoutProps {}

export interface IState extends IBaseCheckoutState {
  currentStepId: string
  completedStepIds: string[]
  // Held outside the serialized checkout/cashier object so it survives the step
  // unmounting (CD-167) without leaking into analytics/error payloads.
  password: string
  // Client-side comprehension gate, never posted; hoisted so it survives the
  // payment body unmounting on step navigation.
  chargeAcknowledged: boolean
  termsAccepted: boolean
}

export function entryStepId(checkout: Checkout): string {
  if (!checkout.signedIn) {
    return 'account'
  }
  if (!checkout.cashier.default_billing_address) {
    return 'billing'
  }
  return 'payment'
}

// `promo_code` is excluded: it comes from the apply-promo AJAX round-trip and
// renders inline at the promo field, so it must not read as a submit failure.
export function serverCheckoutFailed(errors: any): boolean {
  if (!errors) {
    return false
  }
  return Object.keys(errors).some((field) => field !== 'promo_code')
}

export class TrialRedesignForm extends AbstractMembershipCheckoutForm<IProps, IState> {
  constructor(props: IProps) {
    super(props)

    const base = buildBaseCheckoutState(props)
    // A failed checkout re-renders inline at the payment step, not the entry
    // step, so the user lands on the retry affordance with the error in view.
    const currentStepId = serverCheckoutFailed(base.checkout.cashier.errors)
      ? 'payment'
      : entryStepId(base.checkout)
    const entryIndex = STEPS.findIndex((s) => s.id === currentStepId)
    const completedStepIds = STEPS.slice(0, entryIndex < 0 ? 0 : entryIndex).map((s) => s.id)

    this.state = {
      ...base,
      currentStepId,
      completedStepIds,
      password: '',
      chargeAcknowledged: false,
      termsAccepted: base.checkout.cashier.terms_of_service,
    }
  }

  // Entry step varies by sign-in/billing state, so emit the funnel's first
  // `step_view` on mount rather than only on advance.
  public componentDidMount() {
    super.componentDidMount()
    this.trackStepView(this.state.currentStepId)
  }

  public handleStepSelect = (stepId: string) => {
    if (!this.isCompleted(stepId)) {
      return
    }
    this.goToStep(stepId)
  }

  public goToStep = (stepId: string) => {
    const { currentStepId, completedStepIds } = this.state
    const fromIndex = STEPS.findIndex((s) => s.id === currentStepId)
    const toIndex = STEPS.findIndex((s) => s.id === stepId)
    const completed = completedStepIds.includes(currentStepId)
      ? completedStepIds
      : [...completedStepIds, currentStepId]

    this.setState({ currentStepId: stepId, completedStepIds: completed })

    // First-reach signal: fire once when the visitor first moves forward into a
    // step, so back-then-forward bouncing can't inflate advance counts.
    if (toIndex > fromIndex && !completedStepIds.includes(stepId)) {
      trackStepAdvance(this.stepContext(), fromIndex + 1, toIndex + 1)
    }
    this.trackStepView(stepId)
  }

  public accountEmailChanged = (value: string) => {
    const { checkout: { cashier } } = this.state
    const updatedCashier = update(cashier, { customer: { email: { $set: value } } })
    this.updateCashier(updatedCashier)
  }

  public chargeAcknowledgedChanged = (value: boolean) => {
    this.setState({ chargeAcknowledged: value })
  }

  public termsAcceptedChanged = (value: boolean) => {
    this.setState({ termsAccepted: value })
  }

  public accountPasswordChanged = (value: string) => {
    this.setState({ password: value })
  }

  public billingCustomerChanged = (field: BillingCustomerField, value: string) => {
    const { checkout: { cashier } } = this.state
    const updatedCashier = update(cashier, { customer: { [field]: { $set: value } } })
    this.updateCashier(updatedCashier)
  }

  public purchaseOptionChanged = (name: API.PurchaseOptionNameType) => {
    this.setState({ selectedPurchaseOptionName: name }, () => this.updateTaxInformation())
  }

  // Deliberately NOT hoisted to the shared base (along with
  // `handlePromoCodeResponse`): this is the A/B variant seam, and pulling the
  // promo round-trip up would change the live control cell's code path. Hoist
  // if the experiment graduates and the seam is removed.
  public applyPromoCode = (e: React.SyntheticEvent) => {
    const button = $(e.target)
    const form = button.parents('form').first()
    const { checkout: { cashier } } = this.state

    const updatedCashier = update(cashier, { errors: { $unset: ['promo_code'] } })
    this.updateCashier(updatedCashier)

    $.post({
      url: Routes.igniter_membership_checkout_apply_promo_code_path(),
      data: form.serialize(),
      dataType: 'json',
    }).done(this.handlePromoCodeResponse)
  }

  public ctaText(): string {
    return this.state.checkout.trial ? 'Start Your Trial' : 'Start Your Membership'
  }

  public render() {
    const { checkout } = this.state
    const { trial } = checkout
    const planName = this.selectedPurchaseOption().membershipPlan.name

    return (
      <div className="checkout membership-checkout trial-redesign" data-testid="trial-redesign-form">
        <div className="grid-x">
          <header className="cell trial-redesign__header">
            <h1 className="trial-redesign__title">
              <span className="trial-redesign__title-accent">Wingman</span> Request Form
            </h1>
            <p className="trial-redesign__subtitle" data-testid="trial-redesign-subtitle">
              Just kidding. You're about to sign up for {trial ? 'a free trial of the' : 'our'}{' '}
              <strong>{planName}</strong> plan.
            </p>
          </header>
        </div>

        {/* Intentionally NOT wrapped in `.grid-container`, whose max-width would
            cap and inset it: the accordion must span the full viewport so its
            section rules are full-bleed (see trial-accordion.scss). */}
        <FormFor url={Routes.igniter_membership_checkout_path()} method="post" name={CASHIER_FORM_NAME}>
          {this.renderSubmitErrors()}
          {this.renderAccordion()}
          {this.renderHiddenFields()}
        </FormFor>
      </div>
    )
  }

  protected renderAccordion() {
    const { currentStepId, completedStepIds } = this.state

    return (
      <StepAccordion
        steps={STEPS}
        currentStepId={currentStepId}
        completedStepIds={completedStepIds}
        onStepSelect={this.handleStepSelect}
        renderBody={(step) => this.renderStepContent(step)}
      />
    )
  }

  protected renderStepContent(step: IStepDefinition) {
    return (
      <>
        {step.id === 'payment' && this.state.checkout.trial && this.renderTimeline()}
        <div className={`trial-step__split trial-step__split--${step.id}`}>
          <div className="trial-step__fields">{this.renderStepBody(step)}</div>
          <aside
            className="trial-step__rail trial-redesign__rail"
            data-testid="trial-redesign-rail"
            data-current-step={step.id}
          >
            {this.renderRightRail(step.id)}
          </aside>
        </div>
      </>
    )
  }

  // Rendered here, not inside Step3Payment, so the band can break out of the
  // form column to the full-bleed width the Figma timeline requires.
  protected renderTimeline() {
    const { checkout, taxInformation } = this.state
    const { trial, trialLength } = checkout
    const option = this.selectedPurchaseOption()

    return (
      <div className="trial-step__timeline-band">
        <div className="trial-step__timeline-inner">
          <Timeline
            trial={trial}
            trialLength={trialLength}
            totalIncludedItemsInTrial={option.membershipPlan.totalIncludedItemsInTrial}
            planName={option.membershipPlan.name}
            includedItemsInTrial={option.membershipPlan.includedItemsInTrial}
            paymentDetails={option.paymentDetails}
            taxInformation={taxInformation}
            loyaltyRenewalDiscount={this.loyaltyRenewalDiscount(option)}
          />
        </div>
      </div>
    )
  }

  protected renderRightRail(stepId: string) {
    const { checkout, selectedPurchaseOptionName, taxInformation } = this.state

    return (
      <RightRail
        currentStepId={stepId}
        checkoutTestimonials={checkout.checkoutTestimonials}
        purchaseOptions={checkout.purchaseOptions}
        selectedPurchaseOptionName={selectedPurchaseOptionName}
        onPurchaseOptionChange={this.purchaseOptionChanged}
        taxInformation={taxInformation}
        trial={checkout.trial}
      />
    )
  }

  // Collapsed-step persistence (CD-167): a step's body unmounts when it stops
  // being active, so field values are hoisted into the shell (email onto
  // `cashier.customer.email`, password into `state.password`) and handed back as
  // controlled props. `renderHiddenFields` mirrors them for final submit.
  protected renderStepBody(step: IStepDefinition) {
    if (step.id === 'account') {
      return (
        <Step1Account
          // A new customer serializes with a null email, but the input is
          // controlled and needs a string from the first render — a null flips
          // it uncontrolled→controlled on first keystroke (React warns twice).
          email={this.state.checkout.cashier.customer.email ?? ''}
          password={this.state.password}
          signedIn={this.state.checkout.signedIn}
          onEmailChange={this.accountEmailChanged}
          onPasswordChange={this.accountPasswordChanged}
          onContinue={() => this.goToStep('billing')}
        />
      )
    }

    if (step.id === 'billing') {
      const { checkout } = this.state
      const { cashier, stateSelectOptions, countrySelectOptions } = checkout

      return (
        <Step2Billing
          customer={cashier.customer}
          address={cashier.billing_address}
          stateSelectOptions={stateSelectOptions}
          countrySelectOptions={countrySelectOptions}
          onCustomerChanged={this.billingCustomerChanged}
          onAddressChanged={this.billingAddressChanged}
          onContinue={() => this.goToStep('payment')}
        />
      )
    }

    if (step.id === 'payment') {
      const { checkout, taxInformation, paymentToken } = this.state
      const { cashier, trial, trialLength } = checkout
      const {
        payment_type: paymentType,
        payment_profiles: paymentProfiles,
        payment_profile_id: selectedPaymentProfileId,
        credit_card: creditCard,
        allow_promo_code: allowPromoCode,
        promo_code: promoCode,
        errors,
      } = cashier
      const option = this.selectedPurchaseOption()
      const { paymentDetails } = option

      return (
        <Step3Payment
          trial={trial}
          trialLength={trialLength}
          paymentDetails={paymentDetails}
          taxInformation={taxInformation}
          paymentProfiles={paymentProfiles}
          selectedPaymentProfileId={selectedPaymentProfileId}
          creditCard={creditCard}
          paymentType={paymentType}
          paymentToken={paymentToken}
          allowPromoCode={allowPromoCode}
          promoCode={promoCode}
          promoErrors={errors.promo_code || []}
          applyPromoCode={this.applyPromoCode}
          termsOfService={this.state.termsAccepted}
          chargeAcknowledged={this.state.chargeAcknowledged}
          onTermsAcceptedChange={this.termsAcceptedChanged}
          onChargeAcknowledgedChange={this.chargeAcknowledgedChanged}
          ctaText={this.ctaText()}
          onCashierSubmit={this.onCheckoutFormSubmit}
        />
      )
    }

    return null
  }

  protected handlePromoCodeResponse = (response: API.IPromoCodeResponse) => {
    const { checkout } = this.state
    const { cashier } = checkout
    const { checkout: checkoutResponse } = response
    const { purchase_options: purchaseOptions } = checkoutResponse
    const { promo_code, errors } = checkoutResponse.cashier

    const setErrors = errors.promo_code
      ? { promo_code: { $set: errors.promo_code } }
      : { $unset: ['promo_code'] }

    const newPurchaseOptions = purchaseOptions.map((o) => new PurchaseOption(o))

    const updatedCashier = update(cashier, {
      promo_code: { $set: promo_code },
      errors: setErrors,
    })

    this.setState({
      checkout: {
        ...checkout,
        cashier: updatedCashier,
        purchaseOptions: newPurchaseOptions,
      },
    })
  }

  // The forward-looking renewal discount isn't serialized on its own, so proxy
  // it from the renewal-discount adjustment in the current order's breakdown.
  // Absent (new members with no current membership) it is $0.
  protected loyaltyRenewalDiscount(option: PurchaseOption): number {
    const adjustment = option.paymentDetails.adjustments.find(
      (a) => a.label === RENEWAL_DISCOUNT_LABEL,
    )
    return adjustment ? Math.abs(adjustment.value) : 0
  }

  // Covers both rejection paths: server-side `cashier.errors` from a failed POST
  // (parity with the control form's `cashierErrors()`) and client-side Accept.js
  // tokenization failures. `promo_code` is omitted — it renders inline.
  protected renderSubmitErrors() {
    const { checkout, paymentTokenErrors } = this.state
    const cashierErrors = checkout.cashier.errors || {}

    // Rails serializes `base` as an array of messages, so flatten to one <li>
    // each; rendering the array directly mashes them into a run-on line.
    const serverErrors: React.ReactNode[] = []
    Object.keys(cashierErrors)
      .filter((field) => field !== 'promo_code')
      .forEach((field) => {
        const value = cashierErrors[field]
        const messages = Array.isArray(value) ? value : [value]
        const label = field === 'base' ? '' : `${Inflection.titleize(field)} `
        messages.forEach((message: string, i: number) => {
          serverErrors.push(
            <li key={`${field}-${i}`}>
              {label}
              {message}
            </li>,
          )
        })
      })

    const tokenErrors = (paymentTokenErrors || []).map(({ code, text }) => (
      <li key={code}>Credit/Debit Card: {text}</li>
    ))

    const errors = serverErrors.concat(tokenErrors)
    if (errors.length === 0) {
      return null
    }

    return (
      <div className="cashier-errors" data-testid="trial-redesign-payment-errors" role="alert">
        <h2>Please correct the errors below to continue:</h2>
        <ul>{errors}</ul>
      </div>
    )
  }

  protected renderHiddenFields() {
    const { checkout } = this.state
    const { trial } = checkout
    const selectedPurchaseOption = this.selectedPurchaseOption()
    const monthly: boolean = selectedPurchaseOption.name === 'monthly'
    const { code: planCode } = selectedPurchaseOption.membershipPlan

    return (
      <React.Fragment>
        {planCode ? <input type="hidden" name="plan_code" value={planCode} /> : null}
        <input type="hidden" name="monthly" value={monthly ? 1 : 0} />
        {trial ? <input type="hidden" name="trial" value={1} /> : null}
        {this.renderCollapsedAccountFields()}
        {this.renderCollapsedBillingFields()}
      </React.Fragment>
    )
  }

  // Mirror the hoisted email/password as hidden inputs while the account body is
  // unmounted, so final submit still posts them. Skipped when the step is active
  // (live inputs would duplicate these) or the visitor is signed in (the control
  // variant omits customer fields entirely, so posting them would diverge).
  protected renderCollapsedAccountFields() {
    const { currentStepId, password, checkout } = this.state

    if (currentStepId === 'account' || checkout.signedIn) {
      return null
    }

    const { email } = checkout.cashier.customer

    return (
      <React.Fragment>
        <input type="hidden" name={accountFieldName(CASHIER_FORM_NAME, 'email')} value={email} />
        <input type="hidden" name={accountFieldName(CASHIER_FORM_NAME, 'password')} value={password} />
      </React.Fragment>
    )
  }

  // Mirror the hoisted billing payload while the billing body is unmounted.
  // Skipped when the step is active (would duplicate live inputs), billing isn't
  // complete, or the visitor uses a stored default address (the control posts
  // `use_default_billing_address` instead, so mirroring would diverge).
  protected renderCollapsedBillingFields() {
    const { currentStepId, checkout } = this.state

    if (
      currentStepId === 'billing' ||
      !this.isCompleted('billing') ||
      !this.collectsBillingFromUser()
    ) {
      return null
    }

    const { customer, billing_address: address } = checkout.cashier
    const { first_name, last_name, company } = customer
    const { address1, address2, city, state, province, zip, country } = address
    const customerField = (field: BillingCustomerField) =>
      billingCustomerFieldName(CASHIER_FORM_NAME, field)
    const addressField = (field: AddressFormFields) =>
      billingAddressFieldName(CASHIER_FORM_NAME, field)

    // `billingAddressChanged` $sets only the changed field and never clears its
    // sibling, so a US-then-non-US switch leaves a stale `state`. Gate on country
    // exactly as the live `AddressForm` does, so the mirror doesn't post both.
    const isUSA = country === 'USA'

    return (
      <React.Fragment>
        <input type="hidden" name={customerField('first_name')} value={first_name} />
        <input type="hidden" name={customerField('last_name')} value={last_name} />
        <input type="hidden" name={customerField('company')} value={company} />
        <input type="hidden" name={addressField(AddressFormFields.Country)} value={country} />
        <input type="hidden" name={addressField(AddressFormFields.Address1)} value={address1} />
        <input type="hidden" name={addressField(AddressFormFields.Address2)} value={address2} />
        <input type="hidden" name={addressField(AddressFormFields.City)} value={city} />
        {isUSA && state !== undefined ? (
          <input type="hidden" name={addressField(AddressFormFields.State)} value={state} />
        ) : null}
        {!isUSA && province !== undefined ? (
          <input type="hidden" name={addressField(AddressFormFields.Province)} value={province} />
        ) : null}
        <input type="hidden" name={addressField(AddressFormFields.Zip)} value={zip} />
        {/* Match the live form's `<HiddenField value={false} />`, which
            serializes to "false"; `value={0}` was a needless divergence. */}
        <input type="hidden" name={`${CASHIER_FORM_NAME}[use_default_billing_address]`} value="false" />
      </React.Fragment>
    )
  }

  protected isCompleted(stepId: string): boolean {
    return this.state.completedStepIds.includes(stepId)
  }

  // Read the variant from the assignment map rather than assuming `redesign`;
  // tag a missing assignment `unknown` (not blank) so anomalous rows stay
  // filterable downstream.
  protected stepContext(): IStepContext {
    const { experimentVariants } = this.state.checkout
    return {
      experiment: TRIAL_REDESIGN_EXPERIMENT,
      variant: experimentVariants[TRIAL_REDESIGN_EXPERIMENT] || 'unknown',
    }
  }

  protected trackStepView(stepId: string) {
    const index = STEPS.findIndex((s) => s.id === stepId)
    if (index < 0) {
      return
    }
    trackStepView(this.stepContext(), index + 1, stepId)
  }

  // Mirrors the entry-point rule: a signed-in visitor with a stored default
  // billing address skips the billing step and uses that address instead.
  protected collectsBillingFromUser(): boolean {
    const { signedIn, cashier: { default_billing_address } } = this.state.checkout
    return !(signedIn && default_billing_address)
  }
}
